import { create } from "zustand";
import { supabase } from "src/lib/supabaseClient";
import {
  getAssignedTutorForSession,
  getAvailableTutorsExceptAssigned,
  Tutor,
} from "src/services/tutorAssignmentService";

interface Batch {
  id: string;
  name: string;
  product_type: string;
  product_name: string;
  default_tutor_id: string | null;
  status: string;
  student_id: string;
}

export interface BatchCurriculumUnit {
  id: string;
  batch_id: string;
  unit_id: string;
  custom_tutor_id: string | null;
  status: string;
  name: string;
  domainName?: string;
}

export interface BatchCurriculumObjective {
  id: string;
  objective_id: string;
  name: string;
  description?: string;
  lessonName?: string;
}

interface BookingState {
  batches: Batch[];
  batchUnits: BatchCurriculumUnit[];
  availableObjectives: BatchCurriculumObjective[];
  tutors: Tutor[];
  selectedBatch: string | null;
  selectedUnit: string | null;
  selectedObjective: string | null;
  assignedTutor: Tutor | null;
  selectedTutor: string | null;
  isLoading: boolean;
  error: string | null;
  selectionComplete: boolean;
  showChangeTutor: boolean;
  setSelectedBatch: (batchId: string | null) => void;
  setSelectedUnit: (unitId: string | null) => void;
  setSelectedObjective: (objectiveId: string | null) => void;
  setSelectedTutor: (tutorId: string | null) => void;
  setSelectionComplete: (complete: boolean) => void;
  setShowChangeTutor: (show: boolean) => void;
  fetchStudentBatches: () => Promise<void>;
  fetchBatchUnits: (batchId: string) => Promise<void>;
  fetchObjectives: (unitId: string) => Promise<void>;
  getAssignedTutor: (batchId: string, unitId: string, objectiveId?: string) => Promise<void>;
  loadAlternativeTutors: () => Promise<void>;
  submitTutorChangeRequest: (reason: string) => Promise<boolean>;
}

const getAdminUserIds = async (): Promise<string[]> => {
  const { data, error } = await supabase.from("profiles").select("id").eq("user_type", "admin");
  if (error) throw error;
  return data?.map((admin) => admin.id) || [];
};

export const useBookingStore = create<BookingState>((set, get) => ({
  batches: [], batchUnits: [], availableObjectives: [], tutors: [],
  selectedBatch: null, selectedUnit: null, selectedObjective: null,
  assignedTutor: null, selectedTutor: null, isLoading: false, error: null,
  selectionComplete: false, showChangeTutor: false,

  setSelectedBatch: (batchId) => {
    set({ selectedBatch: batchId, selectedUnit: null, selectedObjective: null, assignedTutor: null,
      selectedTutor: null, selectionComplete: false, batchUnits: [], availableObjectives: [] });
    if (batchId) void get().fetchBatchUnits(batchId);
  },
  setSelectedUnit: (unitId) => {
    set({ selectedUnit: unitId, selectedObjective: null, assignedTutor: null, selectedTutor: null,
      selectionComplete: false, availableObjectives: [] });
    if (!unitId) return;
    void get().fetchObjectives(unitId);
    const { selectedBatch } = get();
    if (selectedBatch) void get().getAssignedTutor(selectedBatch, unitId);
  },
  setSelectedObjective: (objectiveId) => {
    set({ selectedObjective: objectiveId, assignedTutor: null, selectedTutor: null, selectionComplete: false });
    const { selectedBatch, selectedUnit } = get();
    if (selectedBatch && selectedUnit) void get().getAssignedTutor(selectedBatch, selectedUnit, objectiveId || undefined);
  },
  setSelectedTutor: (selectedTutor) => set({
    selectedTutor,
    selectionComplete: Boolean(selectedTutor && get().selectedBatch && get().selectedUnit),
  }),
  setSelectionComplete: (selectionComplete) => set({ selectionComplete }),
  setShowChangeTutor: (showChangeTutor) => set({ showChangeTutor }),

  fetchStudentBatches: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");
      const { data, error } = await supabase.from("batches")
        .select("id, name, product_type, product_name, default_tutor_id, status, student_id")
        .eq("student_id", user.id).eq("status", "active");
      if (error) throw error;
      set({ batches: data || [], isLoading: false });
    } catch (error) {
      console.error("Error fetching student batches:", error);
      set({ error: "Failed to load your batches. Please try again.", isLoading: false });
    }
  },

  fetchBatchUnits: async (batchId) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase.from("batch_curriculum_units").select(`
        id, batch_id, unit_id, custom_tutor_id, status,
        unit:curriculum_units!batch_curriculum_units_unit_id_fkey(
          name, domain:curriculum_domains!curriculum_units_domain_id_fkey(name)
        )
      `).eq("batch_id", batchId).eq("status", "active");
      if (error) throw error;
      const batchUnits = (data || []).map((entry: any) => ({
        id: entry.id, batch_id: entry.batch_id, unit_id: entry.unit_id,
        custom_tutor_id: entry.custom_tutor_id, status: entry.status,
        name: entry.unit?.name || "Unknown unit", domainName: entry.unit?.domain?.name,
      }));
      set({ batchUnits, isLoading: false,
        error: batchUnits.length ? null : "No curriculum units are assigned to this batch." });
    } catch (error) {
      console.error("Error fetching batch curriculum units:", error);
      set({ error: "Failed to load curriculum units for this batch. Please try again.", isLoading: false });
    }
  },

  fetchObjectives: async (unitId) => {
    const { selectedBatch } = get();
    if (!selectedBatch) { set({ error: "Select a batch before selecting an objective." }); return; }
    set({ isLoading: true, error: null });
    try {
      const { data: batchUnit, error: batchUnitError } = await supabase.from("batch_curriculum_units")
        .select("id").eq("batch_id", selectedBatch).eq("unit_id", unitId).eq("status", "active").single();
      if (batchUnitError) throw batchUnitError;
      const { data, error } = await supabase.from("batch_curriculum_objectives").select(`
        id, objective_id, display_order,
        objective:curriculum_objectives!batch_curriculum_objectives_objective_id_fkey(
          title, learning_objective, lesson:curriculum_lessons!curriculum_objectives_lesson_id_fkey(name)
        )
      `).eq("batch_curriculum_unit_id", batchUnit.id).eq("status", "active").order("display_order");
      if (error) throw error;
      set({ availableObjectives: (data || []).map((entry: any) => ({
        id: entry.id, objective_id: entry.objective_id, name: entry.objective?.title || "Unknown objective",
        description: entry.objective?.learning_objective || undefined, lessonName: entry.objective?.lesson?.name,
      })), isLoading: false });
    } catch (error) {
      console.error("Error fetching batch objectives:", error);
      set({ error: "Failed to load objectives for this unit. Please try again.", isLoading: false });
    }
  },

  getAssignedTutor: async (batchId, unitId, objectiveId) => {
    set({ isLoading: true, error: null });
    try {
      const assignedTutor = await getAssignedTutorForSession(batchId, unitId, objectiveId || null);
      set({ tutors: assignedTutor ? [assignedTutor] : [], assignedTutor, selectedTutor: assignedTutor?.id || null,
        isLoading: false, showChangeTutor: false,
        selectionComplete: Boolean(assignedTutor && get().selectedBatch && get().selectedUnit) });
    } catch (error) {
      console.error("Error getting assigned tutor:", error);
      set({ error: "Failed to get assigned tutor. Please try again.", isLoading: false });
    }
  },

  loadAlternativeTutors: async () => {
    set({ isLoading: true });
    try {
      const { selectedBatch, selectedUnit, selectedObjective, assignedTutor } = get();
      if (!selectedBatch || !selectedUnit || !assignedTutor) throw new Error("Missing required selection data");
      const alternatives = await getAvailableTutorsExceptAssigned(
        assignedTutor.id, selectedBatch, selectedUnit, selectedObjective || null,
      );
      set({ tutors: [assignedTutor, ...alternatives], isLoading: false, showChangeTutor: true });
    } catch (error) {
      console.error("Error loading alternative tutors:", error);
      set({ error: "Failed to load alternative tutors. Please try again.", isLoading: false });
    }
  },

  submitTutorChangeRequest: async (reason) => {
    const { selectedTutor, selectedBatch, selectedUnit, selectedObjective } = get();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !selectedTutor || !selectedBatch || !selectedUnit) throw new Error("Missing required data for tutor change request");
    const requestedTutor = get().tutors.find((tutor) => tutor.id === selectedTutor);
    if (!requestedTutor) throw new Error("Selected tutor not found");
    const batch = get().batches.find((item) => item.id === selectedBatch);
    const unit = get().batchUnits.find((item) => item.unit_id === selectedUnit);
    const objective = selectedObjective ? get().availableObjectives.find((item) => item.objective_id === selectedObjective) : null;
    const { data: profile, error: profileError } = await supabase.from("profiles")
      .select("first_name, last_name").eq("id", user.id).single();
    if (profileError) throw profileError;
    const { NotificationService } = await import("src/services/notificationService");
    await NotificationService.createFromTemplate(user.id, NotificationService.templates.tutorChangeRequestConfirmation());
    const adminUserIds = await getAdminUserIds();
    if (adminUserIds.length) await NotificationService.createBulkFromTemplate(adminUserIds,
      NotificationService.templates.tutorChangeRequest(
        `${profile.first_name} ${profile.last_name}`.trim() || "Unknown student", batch?.name || "Unknown batch",
        unit?.name || "Unknown unit", objective?.name || null, requestedTutor.name, reason,
      ));
    return true;
  },
}));
